/**
 * Status effects, living in the ECS's 8 fixed status slots per entity.
 *
 * The design rule here is the same one docs/03 §6 applies to enemies: a status
 * is not a private number on an entity, it is a thing that exists in the world.
 * BURNING heats the cells you stand in, so a burning enemy walking through oil
 * lights the oil. FROZEN makes you brittle, so the hammer's shatter is a
 * material property rather than a scripted interaction. SHOCKED conducts into
 * wet neighbours, so a puddle is a battlefield decision.
 *
 * That coupling is why this file imports CellWorld: without it, statuses would
 * be a parallel, invisible system sitting next to the simulation instead of
 * inside it.
 */

import { hash2f } from '../core/rng';
import type { Rng } from '../core/rng';
import { Comp, DamageType, Team, World } from '../entity/ecs';
import type { DamageTypeId } from '../entity/ecs';
import { Category, Flag, M, Tag, VOID } from '../sim/materials';
import { MAT } from '../sim/materialData';
import type { CellWorld } from '../sim/world';
import { StatusKind } from './types';
import type { StatusKindId } from './types';

const SLOTS = World.STATUS_SLOTS;

/**
 * Per-status tuning. `dot` is health per second at one stack; `stackDot` is the
 * additional per-second cost of every stack past the first, kept separate so
 * stacking is sub-linear and 8 stacks of BLEEDING is not instant death.
 *
 * `dotType` exists so a death by burn is reported as a *fire* death rather than
 * as generic damage: docs/06 §4 makes naming the cause a requirement, and a
 * status that damages you anonymously cannot be named.
 */
interface StatusTuning {
  dot: number;
  stackDot: number;
  maxStacks: number;
  /** Statuses that cannot coexist — applying one clears the other. */
  opposes: StatusKindId;
  dotType: DamageTypeId;
}

const T: StatusTuning[] = [];
function tune(
  k: StatusKindId,
  dot: number,
  stackDot: number,
  maxStacks: number,
  opposes: StatusKindId = StatusKind.NONE,
  dotType: DamageTypeId = DamageType.PHYSICAL,
): void {
  T[k] = { dot, stackDot, maxStacks, opposes, dotType };
}

tune(StatusKind.NONE, 0, 0, 0);
tune(StatusKind.BURNING, 5.0, 2.0, 5, StatusKind.FROZEN, DamageType.FIRE);
tune(StatusKind.FROZEN, 0, 0, 4, StatusKind.BURNING, DamageType.FROST);
tune(StatusKind.SHOCKED, 2.0, 1.0, 4, StatusKind.NONE, DamageType.SHOCK);
tune(StatusKind.CORRODED, 3.5, 1.5, 6, StatusKind.NONE, DamageType.ACID);
tune(StatusKind.BLEEDING, 4.0, 1.5, 8, StatusKind.NONE, DamageType.BLOOD);
tune(StatusKind.INFECTED, 2.5, 1.2, 6, StatusKind.NONE, DamageType.FUNGAL);
tune(StatusKind.MARKED, 0, 0, 3);
tune(StatusKind.PHASED, 0, 0, 2);
tune(StatusKind.STAGGERED, 0, 0, 3);
tune(StatusKind.OILED, 0, 0, 4, StatusKind.BURNING);
tune(StatusKind.CURSED, 1.5, 0.8, 5, StatusKind.NONE, DamageType.VOID);
tune(StatusKind.IRRADIATED, 3.0, 1.4, 8, StatusKind.NONE, DamageType.TOXIC);
tune(StatusKind.SLOWED, 0, 0, 3);
tune(StatusKind.HASTED, 0, 0, 3);
tune(StatusKind.WEAKENED, 0, 0, 4);
tune(StatusKind.ARMOR_SHRED, 0, 0, 10);
tune(StatusKind.LIFELINK, 0, 0, 1);
tune(StatusKind.RESONATING, 0, 0, 5);
tune(StatusKind.ECHOED, 0, 0, 3);
tune(StatusKind.NANITE_BOUND, 1.0, 0.5, 5, StatusKind.NONE, DamageType.NANITE);
tune(StatusKind.OVERHEATED, 6.0, 2.5, 3, StatusKind.NONE, DamageType.FIRE);
tune(StatusKind.SPORED, 1.5, 0.7, 6, StatusKind.NONE, DamageType.FUNGAL);
// The AI-side statuses. WET opposes BURNING in both directions by construction:
// applying either clears the other, which is what makes a water hose a fire
// extinguisher without anything anywhere naming "extinguish".
tune(StatusKind.POISONED, 3.0, 1.2, 6, StatusKind.NONE, DamageType.TOXIC);
tune(StatusKind.WET, 0, 0, 3, StatusKind.BURNING);
tune(StatusKind.ENRAGED, 0, 0, 3);
tune(StatusKind.ATTACHED, 0, 0, 1);
tune(StatusKind.BUFFED, 0, 0, 3);
// Issue #175: same magnitude as POISONED at 1 stack — a body that never
// surfaces climbs the same five rungs POISONED does, over the 6 s
// `game.ts` feeds the ladder across.
tune(StatusKind.DROWNING, 3.0, 1.2, 5, StatusKind.NONE, DamageType.PHYSICAL);

/**
 * Issue #175, split from #159/#174. `active` is the caller's own "past the
 * drowning threshold and this archetype can drown" decision
 * (`w.submersion[i] > DEEP_WATER_SUBMERSION && Survivability.drowns`, in
 * `game.ts`'s `stepMovement`) — this function owns only the ladder, kept
 * decoupled from `entity/movement.ts`'s threshold constant the same way
 * `applyAmbientStatus`'s other callers already separate "is the hazard
 * present" from "climb the rung". Exported so a test can drive the ladder
 * directly without a `Game` instance.
 */
export function applyDrowning(w: World, i: number, active: boolean, dt: number): void {
  if (!active) return;
  applyAmbientStatus(w, i, StatusKind.DROWNING, dt, 6.0, 5);
}

function tuning(k: number): StatusTuning {
  return T[k] ?? T[0];
}

/** Max stacks the tuning table allows. Callers may clamp lower, never higher. */
export function maxStacksOf(kind: StatusKindId): number {
  return tuning(kind).maxStacks;
}

/**
 * Reverse index of `opposes`, built once. Opposition has to be symmetric or the
 * result depends on application order — water would extinguish fire but fire
 * would never dry you off, and the player would rightly call that a bug.
 */
const OPPOSED_BY: number[][] = [];
for (let k = 0; k < T.length; k++) {
  const t = T[k];
  if (t === undefined || t.opposes === StatusKind.NONE) continue;
  (OPPOSED_BY[t.opposes] ??= []).push(k);
}

// ------------------------------------------------------------ slot access

/** Slot index holding `kind`, or -1. */
export function findStatus(w: World, i: number, kind: StatusKindId): number {
  const base = i * SLOTS;
  for (let s = 0; s < SLOTS; s++) {
    if (w.statusKind[base + s] === kind && w.statusTime[base + s] > 0) return base + s;
  }
  return -1;
}

export function hasStatus(w: World, i: number, kind: StatusKindId): boolean {
  return findStatus(w, i, kind) >= 0;
}

export function statusStacks(w: World, i: number, kind: StatusKindId): number {
  const s = findStatus(w, i, kind);
  return s < 0 ? 0 : w.statusStacks[s];
}

export function statusTime(w: World, i: number, kind: StatusKindId): number {
  const s = findStatus(w, i, kind);
  return s < 0 ? 0 : w.statusTime[s];
}

/**
 * Seconds of *ambient* exposure stored for `kind` — the meter, not the tail.
 *
 * Separate from `statusTime` on purpose (#120); see `World.statusExposure`.
 * Exported so a test can pin the meter directly instead of inferring it from a
 * duration that a discrete application is also allowed to write.
 */
export function ambientExposure(w: World, i: number, kind: StatusKindId): number {
  const s = findStatus(w, i, kind);
  return s < 0 ? 0 : w.statusExposure[s];
}

/**
 * Apply or refresh a status. Duration takes the max rather than summing, so
 * spamming a fast primer weapon deepens stacks but cannot produce a
 * minute-long burn from a dagger.
 */
export function applyStatus(
  w: World,
  i: number,
  kind: StatusKindId,
  duration: number,
  stacks = 1,
  maxStacks?: number,
): void {
  if (kind === StatusKind.NONE || duration <= 0) return;
  const t = tuning(kind);
  const cap = maxStacks === undefined ? t.maxStacks : Math.min(maxStacks, t.maxStacks);

  if (t.opposes !== StatusKind.NONE) removeStatus(w, i, t.opposes);
  const rev = OPPOSED_BY[kind];
  if (rev !== undefined) for (const o of rev) removeStatus(w, i, o as StatusKindId);

  const existing = findStatus(w, i, kind);
  if (existing >= 0) {
    w.statusTime[existing] = Math.max(w.statusTime[existing], duration);
    w.statusStacks[existing] = Math.min(cap, w.statusStacks[existing] + stacks);
    return;
  }

  const base = i * SLOTS;
  let slot = -1;
  let weakest = -1;
  let weakestTime = Infinity;
  for (let s = 0; s < SLOTS; s++) {
    if (w.statusTime[base + s] <= 0 || w.statusKind[base + s] === StatusKind.NONE) {
      slot = base + s;
      break;
    }
    if (w.statusTime[base + s] < weakestTime) {
      weakestTime = w.statusTime[base + s];
      weakest = base + s;
    }
  }
  // Full: evict whatever expires soonest. Dropping the new application instead
  // would make the 9th status silently do nothing, which reads as a bug.
  if (slot < 0) slot = weakest;
  if (slot < 0) return;

  w.statusKind[slot] = kind;
  w.statusTime[slot] = duration;
  w.statusStacks[slot] = Math.min(cap, stacks);
  // A fresh or evicted slot carries no ambient exposure. Without this, a slot
  // recycled from an expired POISONED would hand its leftover meter to the
  // BURNING that replaced it, which is #120 rebuilt out of slot reuse.
  w.statusExposure[slot] = 0;
  w.add(w.handleAt(i), Comp.STATUS);
}

/**
 * Seconds of affliction bought by one second of contact with a hazard, *plus*
 * the one this frame's decay just spent.
 *
 * It is 2 because one of those two units is a refund, not because contact is
 * twice as toxic. `tickStatuses` runs before the contact pass and has already
 * charged `dt` against every live status by the time an ambient source gets to
 * speak, so a source that wanted to hold a body steady would have to pay `dt`
 * merely to stand still. Paying `2 * dt` nets exactly `dt`: **a second in the
 * cloud is a second of poison, and a second out of it is a second back.** The
 * ramp up and the ramp down are the same slope, which is the property that
 * makes the stack readout a warning the player can act on.
 *
 * The composed behaviour — not this constant — is what
 * `tests/ambientAffliction.test.ts` pins, so if that ordering ever changes the
 * suite reddens rather than the poison silently doubling.
 */
export const AMBIENT_CHARGE_RATE = 2;

/**
 * Apply an affliction that a body is *standing in*, at the rate it is standing
 * in it.
 *
 * The distinction from `applyStatus` is the whole point. `applyStatus` models a
 * discrete event — a hit lands, a threshold is crossed — and adding a whole
 * stack per call is right for it. An ambient source is not an event, it is a
 * condition sampled once per frame, so calling `applyStatus` from one adds a
 * stack per *frame*: the ladder gets climbed at frame rate, a tenth of a second
 * of contact is indistinguishable from ten seconds of it, and the affliction a
 * given walk leaves behind depends on the machine it ran on. That was the
 * measured defect behind "the poison is way too effective. User quickly dies
 * without a chance to recover" — POISONED hit its cap in five frames.
 *
 * Here an *exposure meter* stands in for the frame count. Contact charges it
 * toward `fullDuration`; `tickStatuses` drains it when contact stops; and the
 * stack count is a reading of how full it is. So the tail is automatically
 * sized by the exposure that earned it — a brush leaves a brief single stack, a
 * soak leaves the cap for as long as the soak lasted — without anything needing
 * to remember how long the body was in the cloud.
 *
 * Every other rate in this file and in the contact pass is authored per second
 * and multiplied by `dt`. This is that same conversion, for the one rate that
 * never got it.
 *
 * THE METER IS `w.statusExposure`, NOT `w.statusTime`, and #120 is the ticket
 * for why that distinction is load-bearing rather than tidy. The meter used to
 * *be* the stored duration, which is a faithful reading only while an ambient
 * source is the sole author of the status. It is not: BURNING is applied
 * discretely from `resolve.ts`, from three modules and from `enemyAttacks.ts`,
 * and POISONED from `sporecaller`. Any of those writes a duration longer than
 * this call site's own `fullDuration`, and the meter then reads *full* on a
 * body that has never touched the hazard — so one tick of contact lifted it
 * straight to the ambient cap. Measured: a module's BURNING (3.0 s, one stack)
 * plus a single tick of a burning cell read 5 stacks, against the two seconds
 * the ladder is authored to take, and that tick plus its tail cost 122.21 HP of
 * a 100 HP body.
 *
 * With storage of its own the two facts stay apart. `heldTime` is how long the
 * status lasts and belongs to whoever set it; `exposure` is how long this body
 * has stood in this hazard and belongs to this function. A discrete
 * application deliberately does not touch the second one — that is the whole
 * property — so a weapon burn plus a candle now climbs the ambient ladder from
 * its first rung, with the discrete stack count as a floor beneath it.
 */
export function applyAmbientStatus(
  w: World,
  i: number,
  kind: StatusKindId,
  dt: number,
  fullDuration: number,
  maxStacks?: number,
): void {
  if (kind === StatusKind.NONE || dt <= 0 || fullDuration <= 0) return;
  const t = tuning(kind);
  const cap = maxStacks === undefined ? t.maxStacks : Math.min(maxStacks, t.maxStacks);
  if (cap <= 0) return;

  const existing = findStatus(w, i, kind);
  const heldTime = existing >= 0 ? w.statusTime[existing] : 0;
  const heldStacks = existing >= 0 ? w.statusStacks[existing] : 0;
  // The meter, and *only* the meter. `heldTime` is next to it and is not it.
  const exposure = existing >= 0 ? w.statusExposure[existing] : 0;
  // Whole stacks, because the HUD prints this number. The ladder therefore has
  // `cap` rungs spaced `fullDuration / cap` apart, and any contact at all puts
  // a body on the first one — a hazard that costs nothing to touch teaches the
  // player it is scenery.
  const charged = Math.min(fullDuration, exposure + dt * AMBIENT_CHARGE_RATE);
  const rung = Math.min(cap, Math.max(1, Math.ceil((charged / fullDuration) * cap)));

  // AN AMBIENT SOURCE MAY ONLY EVER ADD. Other things apply these same statuses
  // as discrete events and they aim higher than a cloud does: a `sporecaller`'s
  // toxic hit is POISONED for 3.0 s against this call site's 2.0, and the fungal
  // modules apply INFECTED for up to 6.0 s at two stacks against 3.0 at one.
  // Without these two maxima, walking through a wisp of gas would *cure* the
  // deeper affliction down to the cloud's own ceiling — the cloud would be a
  // reason to seek out gas after being hit, which is the opposite of the rule.
  //
  // Within pure ambient use both maxima are no-ops: `charged` only grows while
  // contact continues, so the ladder is monotone and this costs nothing.
  //
  // Of the two, ONLY THE STACK MAXIMUM IS MECHANISM, and #120 did not change
  // that even though it looks like it should have. The tempting reading is:
  // `charged` used to be derived from the stored duration and so could only
  // exceed it, whereas now it starts from zero and can be far *smaller*, which
  // must make this `Math.max` the line that stops a candle cutting a
  // five-second weapon burn. It is not. `applyStatus` maxes the duration it is
  // handed against the stored one, and the stored one is still exactly
  // `heldTime`, so `max(heldTime, max(heldTime, charged))` and
  // `max(heldTime, charged)` are the same number — deleting this maximum still
  // changes nothing observable, for the same reason as before the meter moved.
  // Re-checked by mutation for #120 rather than inherited from #110's note.
  //
  // It is kept because the rule reads as a pair and a reader who deletes the
  // other half needs this one to still be here. Nobody should count it as a
  // second guard.
  const duration = Math.max(heldTime, charged);
  const stacks = Math.max(heldStacks, rung);

  // Route through `applyStatus` so opposition, slot allocation and eviction
  // stay in exactly one place. It takes the *max* of the durations, which
  // `duration` already is; it *adds* stacks, so the level is written back over
  // the top.
  applyStatus(w, i, kind, duration, stacks, cap);
  const slot = findStatus(w, i, kind);
  if (slot < 0) return;
  w.statusStacks[slot] = Math.min(cap, stacks);
  // Written last, and after the `applyStatus` round trip, because that call may
  // have allocated a fresh slot (which zeroes the meter) or evicted into a
  // different one. `charged` is the meter's new reading either way.
  w.statusExposure[slot] = charged;
}

export function removeStatus(w: World, i: number, kind: StatusKindId): void {
  const s = findStatus(w, i, kind);
  if (s < 0) return;
  w.statusKind[s] = StatusKind.NONE;
  w.statusTime[s] = 0;
  w.statusStacks[s] = 0;
  w.statusExposure[s] = 0;
}

export function clearStatuses(w: World, i: number): void {
  const base = i * SLOTS;
  for (let s = 0; s < SLOTS; s++) {
    w.statusKind[base + s] = StatusKind.NONE;
    w.statusTime[base + s] = 0;
    w.statusStacks[base + s] = 0;
    w.statusExposure[base + s] = 0;
  }
}

// ---------------------------------------------------------- derived queries

/**
 * Incoming-damage multiplier from the target's own statuses. FROZEN is the big
 * one — a frozen body is brittle, which is what makes freeze→shatter a real
 * combo rather than a flavour pairing.
 */
export function damageTakenMul(w: World, i: number): number {
  let m = 1;
  const base = i * SLOTS;
  for (let s = 0; s < SLOTS; s++) {
    if (w.statusTime[base + s] <= 0) continue;
    const st = w.statusStacks[base + s];
    switch (w.statusKind[base + s]) {
      case StatusKind.FROZEN: m *= 1 + 0.22 * st; break;
      case StatusKind.CURSED: m *= 1 + 0.12 * st; break;
      case StatusKind.MARKED: m *= 1 + 0.1 * st; break;
      case StatusKind.ARMOR_SHRED: m *= 1 + 0.05 * st; break;
      case StatusKind.NANITE_BOUND: m *= 1 + 0.08 * st; break;
      case StatusKind.RESONATING: m *= 1 + 0.09 * st; break;
      case StatusKind.WEAKENED: m *= 1.15; break;
      default: break;
    }
  }
  return m;
}

/** Extra multiplier applied specifically to physical/impact damage. */
export function brittleMul(w: World, i: number): number {
  const st = statusStacks(w, i, StatusKind.FROZEN);
  return st > 0 ? 1 + 0.35 * st : 1;
}

/**
 * A frozen body that takes a big enough impact does not take damage, it comes
 * apart. Returns true if the hit shattered it, having sprayed the debris.
 */
export function shatterFrozen(
  w: World,
  cells: CellWorld,
  i: number,
  impact: number,
  rng: Rng,
): boolean {
  const st = statusStacks(w, i, StatusKind.FROZEN);
  if (st <= 0) return false;
  // Threshold falls as stacks rise: deeply frozen things are trivial to break.
  if (impact < w.maxHp[i] * (0.34 - 0.05 * st)) return false;

  removeStatus(w, i, StatusKind.FROZEN);
  const cx = w.x[i];
  const cy = w.y[i];
  const n = 8 + Math.floor(st * 4);
  for (let k = 0; k < n; k++) {
    const a = rng.range(0, Math.PI * 2);
    const r = rng.range(0, Math.max(w.halfW[i], w.halfH[i]));
    const x = Math.round(cx + Math.cos(a) * r);
    const y = Math.round(cy + Math.sin(a) * r);
    if (cells.peek(x, y) === VOID) cells.set(x, y, MAT.ICE, -30);
  }
  applyStatus(w, i, StatusKind.STAGGERED, 1.2, 2);
  return true;
}

/**
 * `moveSpeedMul` was here, and #119 deleted it rather than wiring it.
 *
 * It returned `0.6` for SLOWED, `0.45` for FROZEN and `1.25` for HASTED, and
 * nothing in `src/` ever called it. #110 measured around it and left it; this
 * note is the decision that ticket asked for, kept at the site because the
 * tempting move is to put it back and the reasons not to are not visible from
 * the signature.
 *
 * Measured with §5.2's control-loss instrument — distance a body holding a run
 * covers in one second against clean air, at 30 / 60 / 120 Hz steps — for the
 * player (138.0 px clean) and for an enemy holding a chase (55.8 px clean),
 * both with the function wired symmetrically into `applyHorizontal` and
 * `drive`/`driveY`:
 *
 * | status | player, now | player, wired | enemy, now | enemy, wired |
 * | --- | --- | --- | --- | --- |
 * | FROZEN, from cold | 0.386/0.367/0.364 | 0.181/0.168/0.166 | 0.048/0.034/0.024 | **identical** |
 * | FROZEN, from a hit | 1.000 | 0.450 | 0.221/0.111/0.056 | **identical** |
 * | SLOWED (1 or 3 stacks) | 1.000 | 0.600 | 1.000 | 0.610 |
 * | HASTED | 1.000 | 1.250 | 1.000 | 1.245 |
 *
 * THE FROZEN ROWS ARE THE VERDICT. A frozen enemy does not reach `drive()` at
 * all: `sharedTransitions` in `behaviors.ts` sees FROZEN, drags `vx` and
 * returns, so the wire is unreachable code for the fifty-four bodies FROZEN
 * mostly lands on — the enemy columns are identical to three figures, wired and
 * not. What it does reach is the player, whose cold drag §5.2 measured, banded
 * and pinned as *not moving*; the wire deepens it 2.19x. That is a one-sided
 * nerf under cover of a cleanup, which is precisely what #110 declined to do,
 * and it is now a measurement rather than an argument.
 *
 * SLOWED is the one branch that would have worked, and it is a *feature*.
 * Nothing reads SLOWED today, so `temp_payload_stasis` — an exotic whose melee
 * copy promises "Everything inside it moves at 40% speed" and whose risk warns
 * "The slow field does not distinguish between bodies. Including yours." — is
 * entirely inert. Wiring this function would have given it 0.6x rather than the
 * 0.4x it advertises, with no stack ramp at all (the table above is identical
 * at one stack and at three, because the function never read stacks) despite
 * the module applying two. A module getting its mechanic back deserves its own
 * ticket and its own before/after, not a line in a dead-symbol sweep.
 *
 * HASTED has no writer anywhere in `src/` either — enum entry, tuning row, HUD
 * glyph, and that is all. Wiring a reader for it adds a second dead end to the
 * first.
 *
 * Deleting it moves no number: every cell in the "now" columns is what the game
 * produces with the function present, because nothing called it.
 */

// ------------------------------------------------------------------- tick

/** Scratch, module-level: `tickStatuses` runs every frame and must not allocate. */
const scratchShock = { count: 0 };

/**
 * Damage sink for status ticks. Supplying one routes burn/bleed/poison deaths
 * through the same door as every other kill, which is the only way
 * `onPlayerDeath` can name what killed you. Without it the tick writes hp
 * directly, which is correct for headless tests and wrong for the game.
 */
export type StatusDamageSink = (i: number, amount: number, type: DamageTypeId) => void;

/**
 * Advance every status on every entity, apply damage-over-time, and let the
 * statuses write back into the cell simulation.
 *
 * `rng` is optional: without one, position-stable hashing is used for the
 * scatter decisions, which keeps the whole tick deterministic without
 * consuming PRNG state that replay depends on.
 */
export function tickStatuses(
  w: World,
  cells: CellWorld,
  dt: number,
  rng?: Rng,
  sink?: StatusDamageSink,
): void {
  if (dt <= 0) return;

  w.each(Comp.STATUS, (i) => {
    const base = i * SLOTS;
    let any = false;
    let dot = 0;
    // The heaviest damage-over-time decides what the death line says. Summing
    // the numbers and losing the cause would leave "killed by the world".
    let dotType: DamageTypeId = DamageType.PHYSICAL;
    let dotWorst = 0;

    for (let s = 0; s < SLOTS; s++) {
      const kind = w.statusKind[base + s];
      if (kind === StatusKind.NONE) continue;
      let time = w.statusTime[base + s];
      if (time <= 0) {
        w.statusKind[base + s] = StatusKind.NONE;
        w.statusStacks[base + s] = 0;
        w.statusExposure[base + s] = 0;
        continue;
      }
      // Hitstop freezes statuses too — otherwise a long freeze frame is a free
      // damage tick, and heavy weapons would out-damage light ones on burn.
      //
      // The ambient exposure meter drains on exactly the same gate, and it has
      // to be the same gate rather than merely a similar one:
      // `applyAmbientStatus` charges `2 * dt` on the understanding that this
      // pass already spent one of those units earlier in the same frame (see
      // `AMBIENT_CHARGE_RATE`). Drain the meter on a different condition from
      // the one the refund assumes and a hitstopped body in a fire climbs the
      // ladder at double rate, or a normal one stops climbing at all.
      if (w.hitstop[i] <= 0) {
        time -= dt;
        const ex = w.statusExposure[base + s];
        if (ex > 0) w.statusExposure[base + s] = ex > dt ? ex - dt : 0;
      }
      w.statusTime[base + s] = time;
      const stacks = w.statusStacks[base + s];
      any = true;

      const t = tuning(kind);
      if (t.dot > 0) {
        const d = t.dot + t.stackDot * (stacks - 1);
        dot += d;
        if (d > dotWorst) {
          dotWorst = d;
          dotType = t.dotType;
        }
      }

      switch (kind) {
        case StatusKind.BURNING:
          burnTick(w, cells, i, stacks, dt);
          break;
        case StatusKind.FROZEN:
          freezeTick(w, cells, i, stacks, dt);
          break;
        case StatusKind.SHOCKED:
          shockTick(w, cells, i, stacks);
          break;
        case StatusKind.CORRODED:
          corrodeTick(w, cells, i, stacks, dt, rng);
          break;
        case StatusKind.BLEEDING:
          bleedTick(w, cells, i, stacks, dt, rng);
          break;
        case StatusKind.INFECTED:
        case StatusKind.SPORED:
          sporeTick(w, cells, i, stacks, dt, rng);
          break;
        case StatusKind.OILED:
          oilTick(w, cells, i, stacks);
          break;
        case StatusKind.WET:
          wetTick(w, cells, i);
          break;
        case StatusKind.STAGGERED:
          // Keep the ECS stagger timer alive so movement/AI see one number.
          if (w.stagger[i] < time) w.stagger[i] = time;
          break;
        case StatusKind.PHASED:
          w.bodyFlags[i] |= 1 << 2; // BodyFlag.PHASED
          break;
        default:
          break;
      }
    }

    if (dot > 0 && w.hitstop[i] <= 0) {
      // Composition decides how much a burn actually hurts — a steel knight
      // does not burn like a fungal beast, and no table says so anywhere.
      const amount = dot * dt * dotResistance(w, i);
      if (sink) sink(i, amount, dotType);
      else w.hp[i] -= amount;
    }
    if (!any) w.remove(w.handleAt(i), Comp.STATUS);
  });
}

/**
 * Decay of the combat timers that live on the ECS but are written only by the
 * hit path.
 *
 * `iframes` had no decrementer anywhere in the codebase: `resolveHit` raised it
 * on every connect and gated every subsequent hit on it, so one melee hit made
 * an enemy permanently invulnerable. `movement.ts` only ever raises it with a
 * `Math.max`, and only for the player.
 *
 * `hitstop` and `stagger` live here for the same reason: exactly one system may
 * drain a timer, or it drains at N× and the freeze is N times too short. This
 * is that system for all three.
 */
export function tickCombatTimers(w: World, dt: number): void {
  if (dt <= 0) return;
  w.each(Comp.HEALTH, (i) => {
    if (w.iframes[i] > 0) w.iframes[i] = Math.max(0, w.iframes[i] - dt);
    if (w.hitstop[i] > 0) w.hitstop[i] = Math.max(0, w.hitstop[i] - dt);
    if (w.stagger[i] > 0) w.stagger[i] = Math.max(0, w.stagger[i] - dt);
  });
}

/**
 * Damage-over-time resistance derived from what the entity is made of, not from
 * a per-enemy table (docs/03 §6). Dense, sealed, heat-capacious bodies shrug
 * off sustained damage; porous organic ones do not.
 */
function dotResistance(w: World, i: number): number {
  const c = i * 4;
  let r = 0;
  let total = 0;
  for (let k = 0; k < 4; k++) {
    const frac = w.compFrac[c + k];
    if (frac <= 0) continue;
    const m = w.compMat[c + k];
    total += frac;
    const tg = M.tags[m];
    let res = 1;
    if (tg & Tag.SEALED) res *= 0.7;
    if (tg & Tag.POROUS) res *= 1.3;
    if (tg & Tag.BIOMASS || tg & Tag.ORGANIC) res *= 1.25;
    if (tg & Tag.METAL || tg & Tag.STONE) res *= 0.75;
    res *= 1 / (0.6 + M.heatCapacity[m] * 0.5);
    r += res * frac;
  }
  return total > 0 ? r / total : 1;
}

// ------------------------------------------------------ per-status effects

/** Cell coordinates the entity's body covers, clamped to a small footprint. */
function footprint(w: World, i: number, fn: (x: number, y: number) => void): void {
  const x0 = Math.floor(w.x[i] - w.halfW[i]);
  const x1 = Math.floor(w.x[i] + w.halfW[i]);
  const y0 = Math.floor(w.y[i] - w.halfH[i]);
  const y1 = Math.floor(w.y[i] + w.halfH[i]);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) fn(x, y);
  }
}

/**
 * A burning body is a heat source. That is the whole point — walk through the
 * oil you sprayed and the oil lights, with no rule anywhere saying so.
 *
 * Rate is degrees per second so the result does not depend on frame rate; at
 * one stack that reaches oil's 210°C ignition point in about two thirds of a
 * second, which is the intended "fast, but you can outrun it" feel.
 */
function burnTick(w: World, cells: CellWorld, i: number, stacks: number, dt: number): void {
  const heat = 300 * stacks * dt;
  footprint(w, i, (x, y) => {
    cells.addTemp(x, y, heat);
    const m = cells.peek(x, y);
    if (m !== VOID && M.ignitionTemp[m] < 20000) cells.addFlag(x, y, Flag.BURNING);
  });
}

/** A frozen body chills its surroundings and can freeze the water it stands in. */
function freezeTick(w: World, cells: CellWorld, i: number, stacks: number, dt: number): void {
  const chill = 110 * stacks * dt;
  footprint(w, i, (x, y) => {
    cells.addTemp(x, y, -chill);
    const m = cells.peek(x, y);
    if (m !== VOID && M.freezesAt[m] > -30000 && cells.getTemp(x, y) <= M.freezesAt[m]) {
      cells.addFlag(x, y, Flag.FROZEN);
    }
  });
}

/**
 * Charge does not stay on the body. It goes into whatever conducts — the wet
 * floor, the metal walkway — and from there into whoever else is standing in it.
 */
function shockTick(w: World, cells: CellWorld, i: number, stacks: number): void {
  let conducting = false;
  footprint(w, i, (x, y) => {
    const m = cells.peek(x, y);
    if (m === VOID) return;
    if (M.electricConductivity[m] > 0.15 || (cells.getFlags(x, y) & Flag.WET) !== 0) {
      cells.addFlag(x, y, Flag.CHARGED);
      conducting = true;
    }
  });
  if (!conducting) return;

  // Standing in a conductive medium turns you into a relay for everyone else
  // in it. Radius is generous on purpose: puddles should be scary.
  const r = 26 + 6 * stacks;
  const r2 = r * r;
  const sx = w.x[i];
  const sy = w.y[i];
  scratchShock.count = 0;
  w.each(Comp.HEALTH, (j) => {
    if (j === i || scratchShock.count >= 4) return;
    const dx = w.x[j] - sx;
    const dy = w.y[j] - sy;
    if (dx * dx + dy * dy > r2) return;
    if (hasStatus(w, j, StatusKind.SHOCKED)) return;
    const fx = Math.floor(w.x[j]);
    const fy = Math.floor(w.y[j] + w.halfH[j]);
    const m = cells.peek(fx, fy);
    if (m === VOID) return;
    const wet = (cells.getFlags(fx, fy) & Flag.WET) !== 0;
    if (!wet && M.electricConductivity[m] < 0.15) return;
    applyStatus(w, j, StatusKind.SHOCKED, 1.4, 1);
    scratchShock.count++;
  });
}

// Scatter rates below are per second and multiplied by dt, so a 30 Hz frame
// does not halve how fast a corroding body eats the floor it stands on.

function corrodeTick(w: World, cells: CellWorld, i: number, stacks: number, dt: number, rng?: Rng): void {
  const p = 0.5 * stacks * dt;
  footprint(w, i, (x, y) => {
    const m = cells.peek(x, y);
    if (m === VOID) return;
    const roll = rng ? rng.float() : hash2f(x, y, i);
    if (roll < p * (1 - M.corrosionResist[m])) cells.damageCell(x, y, 2 + stacks);
  });
}

function bleedTick(w: World, cells: CellWorld, i: number, stacks: number, dt: number, rng?: Rng): void {
  const x = Math.floor(w.x[i]);
  const y = Math.floor(w.y[i] + w.halfH[i] * 0.5);
  const roll = rng ? rng.float() : hash2f(x, y, i * 7 + 1);
  if (roll < 7 * stacks * dt && cells.peek(x, y) === VOID) cells.set(x, y, MAT.BLOOD);
}

function sporeTick(w: World, cells: CellWorld, i: number, stacks: number, dt: number, rng?: Rng): void {
  const x = Math.floor(w.x[i]);
  const y = Math.floor(w.y[i]);
  const roll = rng ? rng.float() : hash2f(x, y, i * 13 + 5);
  if (roll < 3 * stacks * dt && cells.peek(x, y) === VOID) cells.set(x, y, MAT.SPORE_CLOUD);
}

/** Oil on a body is a primer. Contact with anything hot converts it to BURNING. */
function oilTick(w: World, cells: CellWorld, i: number, stacks: number): void {
  let hot = false;
  footprint(w, i, (x, y) => {
    if (hot) return;
    if ((cells.getFlags(x, y) & Flag.BURNING) !== 0 || cells.getTemp(x, y) > 220) hot = true;
  });
  if (hot) {
    removeStatus(w, i, StatusKind.OILED);
    applyStatus(w, i, StatusKind.BURNING, 3.0 + stacks, stacks);
  }
}

/**
 * A wet body wets what it touches, and a wet cell conducts. This is the other
 * half of `shockTick`: without it, WET is a private number on an entity and the
 * puddle you are standing in never learns about it.
 */
function wetTick(w: World, cells: CellWorld, i: number): void {
  footprint(w, i, (x, y) => {
    const m = cells.peek(x, y);
    if (m === VOID) return;
    if ((cells.getFlags(x, y) & Flag.BURNING) !== 0) cells.clearFlag(x, y, Flag.BURNING);
    if (M.category[m] !== Category.GAS) cells.addFlag(x, y, Flag.WET);
  });
}

/** Team-aware helper for callers deciding whether a status may be applied. */
export function isHostile(w: World, a: number, b: number): boolean {
  return w.team[a] !== w.team[b] && w.team[a] !== Team.NEUTRAL && w.team[b] !== Team.NEUTRAL;
}
